import React, { useState } from 'react';
import { ChartType, VisibleScenarios, PresetType, EnhancedAssumptions } from './types';
import { INITIAL_VISIBLE_SCENARIOS, SCENARIO_OPTIONS } from './constants';
import { useAssumptions, useCalculations, useChartData } from './hooks';
import { createCurrencyFormatter } from './utils';
import { Layout, Header, HeroMetrics } from './components/layout';
import { MainChart } from './components/charts';
import { PropertyBasics, GrowthAssumptions, ScenarioSelector } from './components/assumptions';
import { Card, Input, HorizontalSelector } from './components/ui';

function EnhancedApp() {
  const [chartType, setChartType] = useState<ChartType>('total');
  const [visibleScenarios, setVisibleScenarios] = useState<VisibleScenarios>(INITIAL_VISIBLE_SCENARIOS);
  const [currentPreset, setCurrentPreset] = useState<PresetType | 'custom'>('even');

  const {
    assumptions,
    updateAssumption,
    handleNumberInput,
    updateScenario,
    setPreset,
    resetToDefaults
  } = useAssumptions();

  const { results, finalYear, rentAdvantage } = useCalculations(assumptions);
  const chartData = useChartData(results, chartType);
  const formatCurrency = createCurrencyFormatter(assumptions.showHalfOwnership);

  const toggleScenario = (scenario: keyof VisibleScenarios) => {
    setVisibleScenarios(prev => ({ ...prev, [scenario]: !prev[scenario] }));
  };

  // Wrapper functions that mark preset as custom when assumptions change
  const handlePresetChange = (preset: PresetType) => {
    setCurrentPreset(preset);
    setPreset(preset);
  };

  const handleAssumptionUpdate = (field: keyof EnhancedAssumptions, value: any) => {
    setCurrentPreset('custom');
    updateAssumption(field, value);
  };

  const handleNumberInputUpdate = (field: keyof EnhancedAssumptions, value: string) => {
    setCurrentPreset('custom');
    handleNumberInput(field, value);
  };

  const handleScenarioUpdate = (type: string, scenario: string) => {
    setCurrentPreset('custom');
    updateScenario(type, scenario);
  };

  const handleReset = () => {
    setCurrentPreset('even');
    resetToDefaults();
  };

  return (
    <Layout>
      <Header
        onPresetChange={handlePresetChange}
        currentPreset={currentPreset}
        showHalfOwnership={assumptions.showHalfOwnership}
        onOwnershipToggle={() => handleAssumptionUpdate('showHalfOwnership', !assumptions.showHalfOwnership)}
        onReset={handleReset}
      />

      <HeroMetrics
        rentValue={finalYear?.rentScenario.totalEquityReal || 0}
        sellValue={finalYear?.sellScenarios.portfolio80_20.totalValueReal || 0}
        advantage={rentAdvantage}
        formatCurrency={formatCurrency}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Assumptions Panel */}
        <div className="lg:col-span-1 space-y-6">
          <PropertyBasics
            assumptions={assumptions}
            onUpdate={handleAssumptionUpdate}
            onNumberInput={handleNumberInputUpdate}
            onScenarioChange={handleScenarioUpdate}
          />
          
          <GrowthAssumptions
            assumptions={assumptions}
            onScenarioChange={handleScenarioUpdate}
            onCustomValueChange={handleNumberInputUpdate}
          />
          
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Strategy & Taxes</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Rental Income Strategy</label>
                <HorizontalSelector
                  options={[
                    { value: 'invest', label: 'Invest in Stock Portfolio', shortLabel: 'Invest' },
                    { value: 'spend', label: 'Spend/Consume (Live Off Income)', shortLabel: 'Spend' }
                  ]}
                  value={assumptions.rentalIncomeStrategy}
                  onChange={(value) => handleAssumptionUpdate('rentalIncomeStrategy', value)}
                  colorScheme="performance"
                  size="sm"
                  className="mb-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {assumptions.rentalIncomeStrategy === 'spend'
                    ? "Rental income is consumed for living expenses (adds no wealth)"
                    : "Rental income is invested in stocks and grows over time"}
                </p>
              </div>
              
              <Input
                label="Inflation Rate (%)"
                type="number"
                step="0.1"
                value={assumptions.inflationRate || ''}
                onChange={(e) => handleNumberInputUpdate('inflationRate', e.target.value)}
              />
              
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className={`w-4 h-4 border flex items-center justify-center ${assumptions.depreciation ? 'bg-gray-900 border-gray-900' : 'border-gray-400'}`}>
                    {assumptions.depreciation && <span className="text-white text-xs">✓</span>}
                  </span>
                  <input type="checkbox" checked={assumptions.depreciation} onChange={() => handleAssumptionUpdate('depreciation', !assumptions.depreciation)} className="sr-only" />
                  <span className="text-sm text-gray-700">Include Depreciation Deduction</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className={`w-4 h-4 border flex items-center justify-center ${assumptions.includeInflation ? 'bg-gray-900 border-gray-900' : 'border-gray-400'}`}>
                    {assumptions.includeInflation && <span className="text-white text-xs">✓</span>}
                  </span>
                  <input type="checkbox" checked={assumptions.includeInflation} onChange={() => handleAssumptionUpdate('includeInflation', !assumptions.includeInflation)} className="sr-only" />
                  <span className="text-sm text-gray-700">Include Inflation Analysis</span>
                </label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Income Tax Rate (%)"
                  type="number"
                  step="1"
                  value={assumptions.rentalIncomeTaxRate || ''}
                  onChange={(e) => handleNumberInputUpdate('rentalIncomeTaxRate', e.target.value)}
                />
                <Input
                  label="Capital Gains (%)"
                  type="number"
                  step="1"
                  value={assumptions.capitalGainsRate || ''}
                  onChange={(e) => handleNumberInputUpdate('capitalGainsRate', e.target.value)}
                />
              </div>
            </div>
          </Card>
          
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Property Expenses</h2>
            <div className="space-y-4">
              <ScenarioSelector
                label="Property Tax Rate"
                scenarioType="propertyTax"
                currentScenario={assumptions.propertyTaxScenario}
                currentValue={assumptions.propertyTaxRate}
                scenarioOptions={SCENARIO_OPTIONS.propertyTax}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
              
              <ScenarioSelector
                label="Maintenance Rate"
                scenarioType="maintenance"
                currentScenario={assumptions.maintenanceScenario}
                currentValue={assumptions.maintenanceRate}
                scenarioOptions={SCENARIO_OPTIONS.maintenanceRate}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
              
              <ScenarioSelector
                label="Vacancy Rate"
                scenarioType="vacancy"
                currentScenario={assumptions.vacancyScenario}
                currentValue={assumptions.vacancyRate}
                scenarioOptions={SCENARIO_OPTIONS.vacancyRate}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
              
              <ScenarioSelector
                label="Management Fee"
                scenarioType="management"
                currentScenario={assumptions.managementScenario}
                currentValue={assumptions.managementFee}
                scenarioOptions={SCENARIO_OPTIONS.managementFee}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
              
              <ScenarioSelector
                label="Insurance Cost"
                scenarioType="insuranceCost"
                currentScenario={assumptions.insuranceCostScenario}
                currentValue={assumptions.insuranceCost}
                scenarioOptions={SCENARIO_OPTIONS.insuranceCost}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
              
              <ScenarioSelector
                label="Selling Costs"
                scenarioType="sellingCosts"
                currentScenario={assumptions.sellingCostsScenario}
                currentValue={assumptions.sellingCosts}
                scenarioOptions={SCENARIO_OPTIONS.sellingCosts}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
              
              <ScenarioSelector
                label="Initial Rental Setup Costs"
                scenarioType="rentalSetupCosts"
                currentScenario={assumptions.rentalSetupCostsScenario}
                currentValue={assumptions.rentalSetupCosts}
                scenarioOptions={SCENARIO_OPTIONS.rentalSetupCosts}
                onScenarioChange={handleScenarioUpdate}
                onCustomValueChange={handleNumberInputUpdate}
              />
            </div>
          </Card>
        </div>

        {/* Main Chart and Results */}
        <div className="lg:col-span-2 space-y-8">
          <MainChart
            chartType={chartType}
            onChartTypeChange={setChartType}
            visibleScenarios={visibleScenarios}
            onScenarioToggle={toggleScenario}
            chartData={chartData}
            formatCurrency={formatCurrency}
          />

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 text-sm">
            <div>
              <p className="text-gray-500">Year 10 Property</p>
              <p className="font-semibold">{formatCurrency(finalYear?.rentScenario.propertyValue || 0)}</p>
            </div>
            <div>
              <p className="text-gray-500">10-Yr Net Income</p>
              <p className="font-semibold">{formatCurrency(finalYear?.rentScenario.cumulativeNetIncome || 0)}</p>
            </div>
            <div>
              <p className="text-gray-500">Gross Rent (10yr)</p>
              <p className="font-semibold">{formatCurrency(results.reduce((sum, year) => sum + year.rentScenario.grossRentalIncome, 0))}</p>
            </div>
            <div>
              <p className="text-gray-500">Total Expenses</p>
              <p className="font-semibold">-{formatCurrency(results.reduce((sum, year) => sum + year.rentScenario.totalExpenses, 0))}</p>
            </div>
          </div>

          {/* Portfolio Comparison */}
          <div className="border-t border-gray-200 pt-4 mb-6">
            <p className="text-sm text-gray-500 mb-3">Alternative Sell Portfolios</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">100% Stocks</p>
                <p className="font-semibold">{formatCurrency(finalYear?.sellScenarios.portfolio100.totalValueReal || 0, true)}</p>
                <p className="text-xs text-gray-400">{assumptions.stockReturns.toFixed(1)}% return</p>
              </div>
              <div>
                <p className="text-gray-600">80/20 Mix</p>
                <p className="font-semibold">{formatCurrency(finalYear?.sellScenarios.portfolio80_20.totalValueReal || 0, true)}</p>
                <p className="text-xs text-gray-400">{((0.8 * assumptions.stockReturns) + (0.2 * assumptions.bondReturns)).toFixed(1)}% return</p>
              </div>
              <div>
                <p className="text-gray-600">60/40 Mix</p>
                <p className="font-semibold">{formatCurrency(finalYear?.sellScenarios.portfolio60_40.totalValueReal || 0, true)}</p>
                <p className="text-xs text-gray-400">{((0.6 * assumptions.stockReturns) + (0.4 * assumptions.bondReturns)).toFixed(1)}% return</p>
              </div>
            </div>
          </div>

          {/* Detailed Analysis Section */}
          <Card padding="lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Scenario Breakdown</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Rental Analysis */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Keep & Rent</h3>
                {finalYear && (
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-2">Property</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Year 10 Value</span>
                          <span className="font-medium">{formatCurrency(finalYear.rentScenario.propertyValue)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Year 10 Annual Rent</span>
                          <span className="font-medium">{formatCurrency(finalYear.rentScenario.annualRent)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Setup Costs</span>
                          <span className="font-medium">-{formatCurrency(assumptions.rentalSetupCosts)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-500 mb-2">Income</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Net Income (10yr)</span>
                          <span className="font-medium">{formatCurrency(finalYear.rentScenario.cumulativeNetIncome)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Strategy</span>
                          <span className="font-medium">{assumptions.rentalIncomeStrategy === 'spend' ? "Spent" : "Invested"}</span>
                        </div>
                        {assumptions.rentalIncomeStrategy === 'invest' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Invested Growth</span>
                            <span className="font-medium">{formatCurrency(finalYear.rentScenario.cumulativeReinvestedIncome)}</span>
                          </div>
                        )}
                        {assumptions.depreciation && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Depreciation</span>
                            <span className="font-medium">{formatCurrency(finalYear.rentScenario.cumulativeDepreciation)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-800">Total Wealth</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(finalYear.rentScenario.totalEquityReal, true)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Investment Analysis */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Sell & Invest</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-2">Sale</p>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Net Proceeds</span>
                        <span className="font-medium">{formatCurrency(assumptions.propertyValue * (1 - assumptions.sellingCosts / 100))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Selling Costs</span>
                        <span className="font-medium">{assumptions.sellingCosts}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-gray-500 mb-2">Portfolio (80/20)</p>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Stock Return</span>
                        <span className="font-medium">{assumptions.stockReturns}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bond Return</span>
                        <span className="font-medium">{assumptions.bondReturns}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Blended</span>
                        <span className="font-medium">{((0.8 * assumptions.stockReturns) + (0.2 * assumptions.bondReturns)).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>

                  {finalYear && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-800">Total Value</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(finalYear.sellScenarios.portfolio80_20.totalValueReal, true)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export default EnhancedApp;